import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { saidaService, SaidaDetalhes, ProdutoSaidaResumido } from '../../services/saida';
import { produtoService } from '../../services/produto';

interface ProdutoAdicionar extends ProdutoSaidaResumido {
  isNew?: boolean;
}

const SaidaAdicionarProduto: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const idSaida = Number(id);

  const [notaSaida, setNotaSaida] = useState<SaidaDetalhes | null>(null);
  const [produtos, setProdutos] = useState<ProdutoAdicionar[]>([]);
  const [produtosDisponiveis, setProdutosDisponiveis] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });
  const [produtosDialogOpen, setProdutosDialogOpen] = useState(false);
  const [produtosParaAdicionar, setProdutosParaAdicionar] = useState<any[]>([]);
  const produtosInicializados = useRef(false);

  const filterProdutos = createFilterOptions({
    stringify: (option: any) => `${option.referencia || ''} ${option.nomeProduto || option.nome || ''}`.toLowerCase(),
    limit: 50,
  });

  const { data: saidaData, isLoading, error } = useQuery<SaidaDetalhes>({
    queryKey: ['saida', idSaida],
    queryFn: () => saidaService.getById(idSaida),
    enabled: !!idSaida,
  });

  useEffect(() => {
    if (saidaData) {
      setNotaSaida(saidaData);
      if (!produtosInicializados.current) {
        produtosInicializados.current = true;
        const produtosExistentes = (saidaData.produtosResumidos || []).map((p) => ({ ...p, isNew: false }));
        setProdutos(produtosExistentes);
      }
    }
  }, [saidaData]);

  useEffect(() => {
    produtosInicializados.current = false;
  }, [idSaida]);

  useEffect(() => {
    if (notaSaida?.id) {
      const carregarProdutos = async () => {
        try {
          const data = await produtoService.getForSelect();
          setProdutosDisponiveis(data);
        } catch (err) {
          console.error('Erro ao carregar produtos disponíveis', err);
        }
      };
      carregarProdutos();
    }
  }, [notaSaida]);

  const getProdutoId = (produto: any): number => {
    return Number(produto.idProduto ?? produto.id ?? produto.IdProduto ?? 0);
  };

  const formatCurrency = (value: number | string) => {
    const numericValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(isNaN(numericValue) ? 0 : numericValue);
  };

  const openProdutosDialog = () => {
    setProdutosParaAdicionar([]);
    setProdutosDialogOpen(true);
  };

  const closeProdutosDialog = () => {
    setProdutosDialogOpen(false);
    setProdutosParaAdicionar([]);
  };

  const handleAdicionarProdutos = () => {
    const novosProdutos: ProdutoAdicionar[] = produtosParaAdicionar.map((p: any) => ({
      id: 0,
      idProduto: getProdutoId(p),
      nomeProduto: p.nomeProduto || p.nome || '',
      referencia: p.referencia || '',
      quantidade: 1,
      precoVenda: (p.precoVenda ?? p.precoVendaString ?? 0).toString(),
      semComissao: p.semComissao === 1 || p.semComissao === true ? 1 : 0,
      isNew: true,
    }));
    setProdutos((prev) => [...prev, ...novosProdutos]);
    setProdutosParaAdicionar([]);
    setProdutosDialogOpen(false);
  };

  const handleProdutoChange = (index: number, produto: any | null) => {
    if (!produto) {
      setSnackbar({ open: true, message: 'Produto não encontrado na lista.', severity: 'error' });
      return;
    }
    const novosProdutos = [...produtos];
    novosProdutos[index] = {
      ...novosProdutos[index],
      idProduto: getProdutoId(produto),
      nomeProduto: produto.nomeProduto || produto.nome || '',
      referencia: produto.referencia || '',
      precoVenda: (produto.precoVenda ?? produto.precoVendaString ?? 0).toString(),
      semComissao: produto.semComissao === 1 || produto.semComissao === true ? 1 : 0,
    };
    setProdutos(novosProdutos);
    setSnackbar({ open: true, message: `Produto selecionado: ${produto.referencia || produto.nomeProduto || produto.nome}`, severity: 'success' });
  };

  const handleQuantidadeChange = (index: number, quantidade: number) => {
    const novosProdutos = [...produtos];
    novosProdutos[index] = { ...novosProdutos[index], quantidade };
    setProdutos(novosProdutos);
  };

  const handleRemoverProduto = (index: number) => {
    const produto = produtos[index];
    if (!produto.isNew) {
      setSnackbar({ open: true, message: 'Não é possível remover produtos já vinculados.', severity: 'error' });
      return;
    }
    setProdutos(produtos.filter((_, i) => i !== index));
  };

  const produtosDisponiveisParaLinha = () => produtosDisponiveis;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const produtosNovos = produtos
        .filter((p) => p.isNew && p.idProduto > 0)
        .map((p) => ({
          ...p,
          id: 0,
          idProduto: Number(p.idProduto),
          quantidade: Number(p.quantidade),
          precoVenda: Number(p.precoVenda),
          semComissao: p.semComissao === 1 || p.semComissao === true ? 1 : 0,
        }));

      if (produtosNovos.length === 0) {
        throw new Error('Adicione pelo menos um produto novo para salvar.');
      }

      const payload = {
        id: idSaida,
        IdRomaneio: notaSaida?.idRomaneio || 0,
        Produtos: produtosNovos,
      };
      return saidaService.adicionarProdutos(payload);
    },
    onSuccess: (data: any) => {
      if (Array.isArray(data)) {
        setSnackbar({ open: true, message: data[0]?.title + ' - ' + data[0]?.detail, severity: 'error' });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['saidas'] });
      queryClient.invalidateQueries({ queryKey: ['saida', idSaida] });
      setSnackbar({ open: true, message: 'Produtos adicionados com sucesso.', severity: 'success' });
      navigate('/saidas');
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: 'Erro ao adicionar produtos: ' + (error.message || error),
        severity: 'error',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate();
  };

  const handleBack = () => {
    navigate('/saidas');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !notaSaida) {
    const errorMessage = (error as any)?.response?.data?.title
      || (error as any)?.response?.data?.message
      || (error as any)?.message
      || 'Erro ao carregar a nota de saída.';
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexDirection: 'column', alignItems: 'center' }}>
        <Typography color="error" gutterBottom>Erro ao carregar a nota de saída.</Typography>
        <Typography color="error" variant="body2">{errorMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Adicionar Produtos - Saída #{idSaida}
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Voltar
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Promotor de vendas"
                value={notaSaida.nomePromotor || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Praça"
                value={notaSaida.praca || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Vendedor(a)"
                value={notaSaida.nomeVendedor || notaSaida.vendedor || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="CPF"
                value={notaSaida.cpf || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Telefone"
                value={notaSaida.telefone || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Endereço"
                value={notaSaida.endereco || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Produtos
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openProdutosDialog}>
              Adicionar Produto
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Preço de Venda</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {produtos.map((produto, index) => (
                  <TableRow key={produto.id || `new-${index}`}>
                    <TableCell>
                      {produto.isNew ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={produto.idProduto ? String(produto.idProduto) : ''}
                            onChange={(e) => {
                              const selected = produtosDisponiveis.find((p) => getProdutoId(p) === Number(e.target.value));
                              handleProdutoChange(index, selected || null);
                            }}
                            displayEmpty
                            renderValue={(selected: any) => {
                              if (!selected) return 'Selecione um produto';
                              const selectedProduct = produtosDisponiveis.find((p) => getProdutoId(p) === Number(selected));
                              return selectedProduct
                                ? (selectedProduct.informacoesExibir || `${selectedProduct.referencia} - ${selectedProduct.nomeProduto || selectedProduct.nome || ''}`)
                                : produto.nomeProduto || 'Selecione um produto';
                            }}
                          >
                            <MenuItem value="">Selecione um produto</MenuItem>
                            {produtosDisponiveisParaLinha().map((p) => (
                              <MenuItem key={getProdutoId(p)} value={String(getProdutoId(p))}>
                                {p.informacoesExibir || `${p.referencia || ''} - ${p.nomeProduto || p.nome || 'Produto sem nome'}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        produto.referencia
                      )}
                    </TableCell>
                    <TableCell>{produto.isNew ? (produto.nomeProduto || '-') : produto.nomeProduto}</TableCell>
                    <TableCell>{formatCurrency(produto.precoVenda)}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={produto.quantidade}
                        onChange={(e) => handleQuantidadeChange(index, Number(e.target.value))}
                        sx={{ width: 100 }}
                        slotProps={{ htmlInput: { min: 1 } }}
                        disabled={!produto.isNew}
                      />
                    </TableCell>
                    <TableCell>
                      {produto.isNew && (
                        <IconButton size="small" color="error" onClick={() => handleRemoverProduto(index)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {produtos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography sx={{ py: 4, color: 'text.secondary' }}>Nenhum produto vinculado.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleBack} disabled={saveMutation.isPending}>
            Voltar
          </Button>
          <Button variant="contained" type="submit" startIcon={<SaveIcon />} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </Box>
      </form>

      <Dialog open={produtosDialogOpen} onClose={closeProdutosDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Produtos</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            fullWidth
            options={produtosDisponiveis}
            getOptionLabel={(option: any) =>
              option.informacoesExibir || `${option.referencia || ''} - ${option.nomeProduto || option.nome || 'Produto sem nome'}`
            }
            value={produtosParaAdicionar}
            onChange={(_, value) => setProdutosParaAdicionar(value)}
            isOptionEqualToValue={(option: any, value: any) => getProdutoId(option) === getProdutoId(value)}
            filterOptions={filterProdutos}
            disabled={produtosDisponiveis.length === 0}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Selecione os produtos"
                placeholder="Pesquise ou selecione..."
              />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProdutosDialog} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleAdicionarProdutos}
            variant="contained"
            disabled={produtosParaAdicionar.length === 0}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SaidaAdicionarProduto;
